import { Link } from 'react-router'
import { CalendarClock, Home, UserRound, User } from 'lucide-react'
import { Modal } from '../ui/Modal'

export interface TimelineEventDetails {
  title: string
  typeLabel: string
  entityLabel: string
  description?: string
  createdAt?: string
  dateLabel?: string
  heading?: string
  location?: string
  detailUrl?: string
  detailUrlLabel?: string
  assigneeName?: string
  priorityLabel?: string
  actorName?: string
  actorUrl?: string
  propertyId?: string
  propertyCode?: string
  propertyAddress?: string
  customerId?: string
  customerName?: string
}

interface TimelineEventDetailsModalProps {
  event: TimelineEventDetails | null
  onClose: () => void
}

export function TimelineEventDetailsModal({ event, onClose }: TimelineEventDetailsModalProps) {
  return (
    <Modal show={event !== null} onClose={onClose} title={event?.heading ?? 'Dettaglio evento'}>
      {event && (
        <div className="space-y-4">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-50 px-2.5 py-1 text-xs font-semibold text-link">{event.typeLabel}</span>
              <span className="rounded-full bg-surface px-2.5 py-1 text-xs font-medium text-muted">{event.entityLabel}</span>
            </div>
            <h4 className="m-0 text-lg font-semibold text-heading">{event.title}</h4>
            {event.description && <p className="mb-0 mt-2 whitespace-pre-line text-sm text-body">{event.description}</p>}
          </div>

          <dl className="m-0 divide-y divide-border rounded-lg border border-border">
            <div className="flex items-start gap-3 px-3 py-2.5 text-sm">
              <CalendarClock size={16} className="mt-0.5 shrink-0 text-muted" />
              <div><dt className="text-xs text-muted">{event.dateLabel ?? 'Quando'}</dt><dd className="m-0 font-medium text-heading">{event.createdAt ? new Date(event.createdAt).toLocaleString('it-IT') : 'Non specificata'}</dd></div>
            </div>
            {event.location && <div className="px-3 py-2.5 text-sm"><dt className="text-xs text-muted">Luogo</dt><dd className="m-0 font-medium text-heading">{event.location}</dd></div>}
            {event.priorityLabel && <div className="px-3 py-2.5 text-sm"><dt className="text-xs text-muted">Priorità</dt><dd className="m-0 font-medium text-heading">{event.priorityLabel}</dd></div>}
            {event.assigneeName && <div className="px-3 py-2.5 text-sm"><dt className="text-xs text-muted">Assegnato a</dt><dd className="m-0 font-medium text-heading">{event.assigneeName}</dd></div>}
            {event.propertyId && (
              <div className="flex items-start gap-3 px-3 py-2.5 text-sm">
                <Home size={16} className="mt-0.5 shrink-0 text-link" />
                <div className="min-w-0"><dt className="text-xs text-muted">Immobile</dt><dd className="m-0"><Link to={`/properties/${event.propertyId}`} onClick={onClose} className="font-medium text-link hover:underline">{event.propertyCode}{event.propertyAddress ? ` · ${event.propertyAddress}` : ''}</Link></dd></div>
              </div>
            )}
            {event.customerId && event.customerName && (
              <div className="flex items-start gap-3 px-3 py-2.5 text-sm">
                <UserRound size={16} className="mt-0.5 shrink-0 text-link" />
                <div><dt className="text-xs text-muted">Cliente</dt><dd className="m-0"><Link to={`/customers/${event.customerId}`} onClick={onClose} className="font-medium text-link hover:underline">{event.customerName}</Link></dd></div>
              </div>
            )}
            {event.actorName && (
              <div className="flex items-start gap-3 px-3 py-2.5 text-sm">
                <User size={16} className="mt-0.5 shrink-0 text-muted" />
                <div><dt className="text-xs text-muted">Registrato da</dt><dd className="m-0">{event.actorUrl ? <Link to={event.actorUrl} onClick={onClose} className="font-medium text-link hover:underline">{event.actorName}</Link> : <span className="font-medium text-heading">{event.actorName}</span>}</dd></div>
              </div>
            )}
          </dl>
          {event.detailUrl && <Link to={event.detailUrl} onClick={onClose} className="inline-flex rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700">{event.detailUrlLabel ?? 'Apri elemento'}</Link>}
        </div>
      )}
    </Modal>
  )
}
